import { useCallback, useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'

const STORAGE_KEY = 'lane-control:archived-groups'
const COLLAPSED_KEY = 'lane-control:archived-groups-collapsed'
const IMPORTED_KEY = 'lane-control:archived-groups-imported'
const PENDING_KEY = 'lane-control:archived-groups-pending'
const ENDPOINT = '/api/ui_prefs/group_archive'

export const DEFAULT_ARCHIVED_GROUP_IDS = [
  'pwa-auto-architecture-deepen',
  'pwa-auto-freelancer',
  'pwa-auto-news',
  'pwa-auto-todoist-tasks',
  'pwa-auto-commitments',
]

function loadIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return DEFAULT_ARCHIVED_GROUP_IDS
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed.filter((v): v is string => typeof v === 'string')
  } catch {/* ignore */}
  return DEFAULT_ARCHIVED_GROUP_IDS
}

function loadCollapsed(): boolean {
  try { return localStorage.getItem(COLLAPSED_KEY) !== 'false' } catch { return true }
}

function loadPending(): Record<string, boolean> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(PENDING_KEY) ?? '{}')
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return Object.fromEntries(Object.entries(parsed).filter(([, value]) => typeof value === 'boolean'))
    }
  } catch {/* fall back to memory */}
  return {}
}

const pending = loadPending()
// Capture before a hydrated query can replace the local render cache.
const legacyIds = loadIds()
let syncing: Promise<string[]> | null = null

function savePending(): void {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify(pending)) } catch {/* retain in memory */}
}

function applyPending(ids: string[]): string[] {
  const result = new Set(ids)
  for (const [id, archived] of Object.entries(pending)) {
    if (archived) result.add(id)
    else result.delete(id)
  }
  return [...result]
}

/** Import each device's old non-default choices without overwriting synced edits,
 * then deliver per-group changes. Failed requests retain the durable pending map. */
export function syncGroupArchive(): Promise<string[]> {
  if (syncing) return syncing
  syncing = (async () => {
    let imported = false
    try { imported = localStorage.getItem(IMPORTED_KEY) === 'true' } catch {/* retry safely */}
    if (!imported) {
      const legacy = legacyIds
      const overrides = [...new Set([...legacy, ...DEFAULT_ARCHIVED_GROUP_IDS])]
        .filter((id) => legacy.includes(id) !== DEFAULT_ARCHIVED_GROUP_IDS.includes(id))
        .map((chat_id) => ({ chat_id, archived: legacy.includes(chat_id) }))
      await api(ENDPOINT + '/import', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrides }), signal: AbortSignal.timeout(10_000),
      })
      try { localStorage.setItem(IMPORTED_KEY, 'true') } catch {/* imports are idempotent */}
    }
    for (const [chat_id, archived] of Object.entries(pending)) {
      await api(ENDPOINT, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id, archived }), signal: AbortSignal.timeout(10_000),
      })
      // A newer drag while the request was in flight still needs delivery.
      if (pending[chat_id] === archived) delete pending[chat_id]
      savePending()
    }
    const result = await api<{ archived_ids: string[] }>(ENDPOINT, { signal: AbortSignal.timeout(10_000) })
    return applyPending(result.archived_ids)
  })().finally(() => { syncing = null })
  return syncing
}

/** Shared display-only archive membership; collapse/expand stays per device. */
export function useArchivedGroups() {
  const [ids, setIds] = useState<string[]>(() => applyPending(loadIds()))
  const [collapsed, setCollapsedState] = useState<boolean>(loadCollapsed)
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: ['group-archive'], queryFn: syncGroupArchive,
    staleTime: 0, refetchInterval: 5_000,
    refetchOnWindowFocus: 'always', refetchOnReconnect: 'always',
    networkMode: 'always', retry: false,
  })

  useEffect(() => {
    if (!query.data) return
    const next = applyPending(query.data)
    setIds(next)
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)) } catch {/* keep rendered state */}
  }, [query.data, query.dataUpdatedAt])

  const setArchived = useCallback((chatId: string, archived: boolean) => {
    pending[chatId] = archived
    savePending()
    setIds((current) => {
      const next = archived
        ? (current.includes(chatId) ? current : [...current, chatId])
        : current.filter((id) => id !== chatId)
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)) } catch {/* ignore */}
      return next
    })
    void qc.invalidateQueries({ queryKey: ['group-archive'] })
  }, [qc])

  const setCollapsed = useCallback((value: boolean) => {
    setCollapsedState(value)
    try { localStorage.setItem(COLLAPSED_KEY, String(value)) } catch {/* ignore */}
  }, [])

  return { archivedIds: ids, setArchived, collapsed, setCollapsed, syncError: query.isError }
}
